import React, { useState } from 'react';
import { 
  Menu, 
  MenuItem, 
  Button, 
  Box,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Language as LanguageIcon, Check } from '@mui/icons-material';
import { useLanguage } from '../../shared/contexts/LanguageContext';

interface LanguageSwitcherProps {
  mobile?: boolean;
}

export const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ mobile = false }) => {
  const { language, setLanguage } = useLanguage();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (lang: 'en' | 'de') => {
    setLanguage(lang);
    handleClose();
  };

  const languageOptions = [
    { code: 'en' as const, label: 'English' },
    { code: 'de' as const, label: 'Deutsch' },
  ];

  if (mobile) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {languageOptions.map((option) => (
          <Button
            key={option.code}
            variant={language === option.code ? 'contained' : 'outlined'}
            onClick={() => setLanguage(option.code)}
            fullWidth
            size="small"
          >
            {option.label}
          </Button>
        ))}
      </Box>
    );
  }

  const currentLanguage = languageOptions.find(opt => opt.code === language);

  return (
    <>
      <Button
        onClick={handleClick}
        variant="text"
        size="small"
        startIcon={<LanguageIcon />}
        sx={{ 
          minWidth: 'auto',
          color: 'text.primary',
          textTransform: 'none',
          fontSize: '1rem',
          fontWeight: 400,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {currentLanguage?.label}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languageOptions.map((option) => (
          <MenuItem
            key={option.code}
            onClick={() => handleLanguageSelect(option.code)}
            selected={language === option.code}
          >
            {language === option.code && (
              <ListItemIcon>
                <Check fontSize="small" />
              </ListItemIcon>
            )}
            <ListItemText 
              primary={option.label}
              sx={{ ml: language === option.code ? 0 : 4 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

